"use client";

import { useEffect, useState } from "react";

import "./lib/fontawesome";
import "./globals.css";
import { SidebarLayout } from "@/components/sidebar-layout";
import SidebarSearchSection from "@/components/SidebarSearchSection";
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from "@/components/dropdown";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/16/solid";
import {
  Navbar,
  NavbarItem,
  NavbarSection,
  NavbarSpacer,
} from "@/components/navbar";
import {
  Sidebar,
  SidebarBody,
  SidebarDivider,
  SidebarFooter,
  SidebarHeader,
  SidebarHeading,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer,
} from "@/components/sidebar";
import { Avatar } from "@/components/avatar";
import { NAV_ITEMS, DROPDOWN_ITEMS, METADATA } from "@/config/config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import { faCircleQuestion } from "@fortawesome/free-regular-svg-icons";
import SearchDialogWrapper from "@/components/SearchDialogWrapper";

export default function RootLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [nextEvents, setNextEvents] = useState<any[]>([]);

  useEffect(() => {
    async function fetchEvents() {
      try {
        const res = await fetch("/api/calendar-events?limit=4");
        const data = await res.json();
        console.log("Fetched events:", data);
        setNextEvents(data);
      } catch (err) {
        console.error("Fehler beim Laden der Termine:", err);
      }
    }

    fetchEvents();
  }, []);

  return (
    <html lang="de">
      <head>
        <link rel="stylesheet" href="https://geists.vercel.app/font.css" />
      </head>
      <body className="font-geist antialiased">
        <SearchDialogWrapper />
        <SidebarLayout
          navbar={
            <Navbar>
              <NavbarSpacer />
              <NavbarSection>
                {NAV_ITEMS.map((item) => (
                  <NavbarItem
                    key={item.href}
                    href={item.href}
                    target={item.blank ? "_blank" : "_self"}
                    aria-label={item.label}
                  >
                    <FontAwesomeIcon icon={item.icon} />
                  </NavbarItem>
                ))}

                <Dropdown>
                  <DropdownButton as={NavbarItem}>
                    <Avatar src="/logo.png" square />
                  </DropdownButton>
                  <DropdownMenu className="min-w-64" anchor="bottom end">
                    {DROPDOWN_ITEMS.map((item, index) =>
                      item.type === "divider" ? (
                        <DropdownDivider key={`divider-${index}`} />
                      ) : (
                        <DropdownItem key={item.href} href={item.href}>
                          <FontAwesomeIcon icon={item.icon} />
                          <DropdownLabel> {item.label}</DropdownLabel>
                        </DropdownItem>
                      )
                    )}
                  </DropdownMenu>
                </Dropdown>
              </NavbarSection>
            </Navbar>
          }
          sidebar={
            <Sidebar>
              <SidebarHeader>
                <Dropdown>
                  <DropdownButton as={SidebarItem} className="lg:mb-2.5">
                    <Avatar src="/logo.png" />
                    <SidebarLabel>
                      Tiergesundheit <br /> Hembergen
                    </SidebarLabel>
                    <ChevronDownIcon />
                  </DropdownButton>
                  <DropdownMenu
                    className="min-w-80 lg:min-w-64"
                    anchor="bottom start"
                  >
                    {DROPDOWN_ITEMS.map((item, index) =>
                      item.type === "divider" ? (
                        <DropdownDivider key={index} />
                      ) : (
                        <DropdownItem key={item.href} href={item.href}>
                          <FontAwesomeIcon icon={item.icon} />
                          <DropdownLabel> {item.label}</DropdownLabel>
                        </DropdownItem>
                      )
                    )}
                  </DropdownMenu>
                </Dropdown>
              </SidebarHeader>
              <SidebarBody>
                <SidebarSection>
                  <SidebarSearchSection />
                </SidebarSection>
                <SidebarDivider />
                <SidebarSection>
                  {NAV_ITEMS.map((item) => (
                    <SidebarItem
                      key={item.href}
                      href={item.href}
                      target={item.blank ? "_blank" : "_self"}
                    >
                      <FontAwesomeIcon icon={item.icon} />
                      <SidebarLabel>{item.label}</SidebarLabel>
                    </SidebarItem>
                  ))}
                </SidebarSection>
                <SidebarSection className="max-lg:hidden">
                  <SidebarHeading>Nächste Termine</SidebarHeading>
                  {nextEvents.length === 0 ? (
                    <SidebarItem>Keine Termine</SidebarItem>
                  ) : (
                    nextEvents.map((event) => (
                      <SidebarItem key={event.id} href="#">
                        {new Date(event.start).toLocaleTimeString("de-DE", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                        : {event.title}
                      </SidebarItem>
                    ))
                  )}
                </SidebarSection>
                <SidebarSpacer />
                <SidebarSection>
                  <SidebarItem href="https://support.it-eden.de">
                    <FontAwesomeIcon icon={faCircleQuestion} />
                    <SidebarLabel>Support</SidebarLabel>
                  </SidebarItem>
                  <SidebarItem href="#">
                    <FontAwesomeIcon icon={faStar} />
                    <SidebarLabel>Version {METADATA.version}</SidebarLabel>
                  </SidebarItem>
                </SidebarSection>
              </SidebarBody>
              <SidebarFooter className="max-lg:hidden">
                <Dropdown>
                  <DropdownButton as={SidebarItem}>
                    <span className="flex min-w-0 items-center gap-3">
                      <Avatar
                        src="/logo.png"
                        className="size-10"
                        square
                        alt=""
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-sm/5 font-medium text-zinc-950 dark:text-white">
                          Christina
                        </span>
                        <span className="block truncate text-xs/5 font-normal text-zinc-500 dark:text-zinc-400">
                          Admin
                        </span>
                      </span>
                    </span>
                    <ChevronUpIcon />
                  </DropdownButton>
                  <DropdownMenu className="min-w-64" anchor="top start">
                    {DROPDOWN_ITEMS.map((item, index) =>
                      item.type === "divider" ? (
                        <DropdownDivider key={index} />
                      ) : (
                        <DropdownItem key={item.href} href={item.href}>
                          <FontAwesomeIcon icon={item.icon} />
                          <DropdownLabel>{item.label}</DropdownLabel>
                        </DropdownItem>
                      )
                    )}
                  </DropdownMenu>
                </Dropdown>
              </SidebarFooter>
            </Sidebar>
          }
        >
          {children}
        </SidebarLayout>
      </body>
    </html>
  );
}
